import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getOcppActiveSocketsForSiteOptions,
  getSocketMappingSuggestionsOptions,
} from "../../../services/client/@tanstack/react-query.gen";

export type SuggestedSocketMapping = {
  socketId: string;
  entries: {
    connectorId: number;
    assetId: string;
    confidence: number;
    nbMatchingTransactions: number;
    nbConsideredTransactions: number;
  }[];
};

export function useSuggestedMappings(
  siteId: string,
  draftMap: Record<string, string[]>,
) {
  const { data: activeSockets, isLoading: isLoadingSockets } = useQuery({
    ...getOcppActiveSocketsForSiteOptions({ path: { site_id: siteId } }),
  });

  const { data: suggestions, isLoading: isLoadingSuggestions } = useQuery({
    ...getSocketMappingSuggestionsOptions({ path: { site_id: siteId } }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const activeSocketIds = useMemo(
    () => new Set(activeSockets?.map((s) => s.id) ?? []),
    [activeSockets],
  );

  // Sockets whose WebSocket is open on the broker right now. An idle charger
  // can stay connected for an hour or more without sending a message.
  const connectedSocketIds = useMemo(
    () =>
      new Set(activeSockets?.filter((s) => s.connected).map((s) => s.id) ?? []),
    [activeSockets],
  );

  const suggestedMappings = useMemo<SuggestedSocketMapping[]>(() => {
    if (!suggestions || suggestions.length === 0) {
      return [];
    }

    return suggestions
      .map((s) => ({
        socketId: s.socket_id,
        entries: s.connectors.map((c) => ({
          connectorId: c.evse_id,
          assetId: c.asset_id,
          confidence: c.confidence,
          nbMatchingTransactions: c.nb_matching_transactions,
          nbConsideredTransactions: c.nb_considered_transactions,
        })),
      }))
      .filter((s) => {
        const currentAssets = draftMap[s.socketId] ?? [];
        const proposedAssets = s.entries.map((e) => e.assetId);
        return JSON.stringify(currentAssets) !== JSON.stringify(proposedAssets);
      });
  }, [suggestions, draftMap]);

  return {
    activeSocketIds,
    connectedSocketIds,
    suggestedMappings,
    isLoadingSockets,
    isLoadingSuggestions,
  };
}
